import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
  useRef,
} from 'react';
import { ChatState, Conversation, SendMessageData } from '../schemas/chat';
import { chatReducer, initialState } from './chatReducer';
import { useConversationActions } from '../hooks/useConversationActions';
import { useMessageActions } from '../hooks/useMessageActions';
import { researchAPI } from '../services/api/researchApi';

// Context interface
interface ChatContextType {
  state: ChatState;
  // Conversation actions
  loadConversations: () => Promise<void>;
  createConversation: (data?: {
    title?: string;
    model_id?: string;
    description?: string;
    knowledge_base_ids?: string[];
    folder_id?: string;
    literature_sources?: string[];
    literature_options?: {
      year_from?: number | null;
      year_to?: number | null;
      max_results?: number | null;
      language?: string | null;
      fields_of_study?: string[] | null;
    };
  }) => Promise<Conversation | null>;
  updateConversation: (id: string, data: Partial<Conversation>) => Promise<void>;
  updateConversationTitle: (id: string, title: string, persistToBackend?: boolean) => void; // Immediate update with optional API call
  deleteConversation: (id: string) => Promise<void>;
  setCurrentConversation: (id: string | null) => void;
  archiveConversation: (id: string) => Promise<void>;
  pinConversation: (id: string, pinned?: boolean) => Promise<void>;
  loadConversationWithMessages: (id: string) => Promise<Conversation | null>;
  // Message actions
  sendMessage: (conversationId: string, data: SendMessageData) => Promise<void>;
  sendMessageStream: (
    conversationId: string,
    data: SendMessageData,
    onChunk?: (chunk: string) => void,
    onThinkingChunk?: (chunk: string) => void,
    onTitleChunk?: (title: string) => void
  ) => Promise<void>;
  resumeMessageStream: (conversationId: string, messageId: string) => Promise<void>;
  updateMessage: (conversationId: string, messageId: string, content: string) => Promise<void>;
  deleteMessage: (conversationId: string, messageId: string) => Promise<void>;
  regenerateMessage: (conversationId: string, messageId: string) => Promise<void>;
  // Utility actions
  clearError: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Provider component
interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  // Track which conversation/message pairs we've already attempted to resume
  const resumedStreamsRef = useRef<Set<string>>(new Set());

  // Utility actions - memoized to prevent unnecessary re-renders
  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  // Conversation actions
  const conversationActions = useConversationActions({
    dispatch,
    setLoading,
    setError,
  });

  // Immediate update conversation title (optimistic update) with optional backend persistence
  const updateConversationTitle = useCallback(
    async (id: string, title: string, persistToBackend: boolean = false) => {
      // Immediately update local state (optimistic)
      dispatch({
        type: 'UPDATE_CONVERSATION',
        payload: { id, title } as any,
      });

      // Optionally persist to backend (fire-and-forget, non-blocking)
      if (persistToBackend) {
        researchAPI.updateResearchChat(id, { title }).catch(error => {
          console.error('Failed to persist title to backend:', error);
          // Note: We don't revert the optimistic update since the backend
          // will eventually return the correct title on next refresh
        });
      }
    },
    [dispatch]
  );

  // Message actions
  const messageActions = useMessageActions({
    dispatch,
    setLoading,
    setError,
    clearError,
    updateConversationTitle,
  });

  // Initialize
  // AuthenticatedProviders ensures this only mounts inside ProtectedRoute
  // So we can safely load conversations without auth checks
  useEffect(() => {
    const initializeConversations = async () => {
      // Load conversations from API (metadata only, no messages)
      await conversationActions.loadConversations();

      // Note: Messages will be loaded from cache or API when user navigates
      // to specific conversation via useConversation hook
    };

    initializeConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-resume incomplete streams when messages are loaded
  useEffect(() => {
    state.conversations.forEach(conversation => {
      if (!conversation.messages || conversation.messages.length === 0) {
        return;
      }

      // Find the last assistant message
      const lastAssistantMessage = [...conversation.messages]
        .reverse()
        .find(m => m.role === 'assistant');

      if (!lastAssistantMessage) {
        return;
      }

      // Only resume if the last assistant message is not done
      if (lastAssistantMessage.is_done === false) {
        // Skip frontend-generated temporary IDs (they start with 'assistant-', 'user-', 'temp-', etc.)
        if (
          lastAssistantMessage.id.startsWith('assistant-') ||
          lastAssistantMessage.id.startsWith('user-') ||
          lastAssistantMessage.id.startsWith('temp-')
        ) {
          console.log(
            `[ChatContext] Skipping resume for temporary message ${lastAssistantMessage.id}`
          );
          return;
        }

        // Check if we're already streaming this conversation - don't resume if actively streaming
        if (
          state.streamingConversationId === conversation.id &&
          state.streamingMessageId === lastAssistantMessage.id
        ) {
          console.log(
            `[ChatContext] Already streaming message ${lastAssistantMessage.id}, skipping resume`
          );
          return;
        }

        const resumeKey = `${conversation.id}-${lastAssistantMessage.id}`;

        // Check if we haven't already attempted to resume this message
        if (!resumedStreamsRef.current.has(resumeKey)) {
          resumedStreamsRef.current.add(resumeKey);

          console.log(
            `[ChatContext] Detected incomplete stream for last assistant message ${lastAssistantMessage.id} in conversation ${conversation.id}. Auto-resuming...`
          );

          // Resume the stream without blocking
          messageActions
            .resumeMessageStream(conversation.id, lastAssistantMessage.id)
            .catch(error => {
              console.error(
                `[ChatContext] Failed to resume stream for message ${lastAssistantMessage.id}:`,
                error
              );
            });
        }
      }
    });
  }, [
    state.conversations,
    messageActions,
    state.streamingConversationId,
    state.streamingMessageId,
  ]);

  const value: ChatContextType = {
    state,
    ...conversationActions,
    ...messageActions,
    updateConversationTitle,
    clearError,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

// Hook
export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export default ChatContext;
